import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { faqItems } from '@/data/mock';
import { RevealSection, RevealText } from '@/components/shared/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const SectionLabel = ({ children }) => (
  <span className="section-label block mb-5">{children}</span>
);

const CopperRule = () => (
  <div className="w-16 h-px bg-copper my-16 md:my-20" />
);

const BulletList = ({ items, className = "" }) => (
  <div className={`space-y-2.5 ${className}`}>
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <div className="w-1 h-1 rounded-full bg-copper mt-2.5 flex-shrink-0" />
        <p className="text-vine-primary/65 text-[15px] leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

const BulletListLight = ({ items, className = "" }) => (
  <div className={`space-y-2.5 ${className}`}>
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-3">
        <div className="w-1 h-1 rounded-full bg-copper mt-2.5 flex-shrink-0" />
        <p className="text-vine-neutral/70 text-[15px] leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

const ClosingLine = ({ children }) => (
  <p className="text-vine-primary/45 text-[14px] italic mt-6">{children}</p>
);

export default function AdvisoryCirclePage() {
  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 md:py-44">
          <div className="max-w-3xl">
            <RevealText>
              <div className="w-14 h-px bg-copper mb-12" />
            </RevealText>
            <RevealText delay={50}>
              <SectionLabel>Advisory Circle</SectionLabel>
            </RevealText>
            <RevealText delay={150}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-[5.5rem] font-light leading-[1.05] tracking-tight mt-4">
                A Governed Advisory Structure for Operators
              </h1>
            </RevealText>
            <RevealText delay={300}>
              <p className="mt-10 text-lg md:text-xl text-vine-neutral/70 leading-relaxed max-w-xl font-light">
                Privacy-first. Decision-oriented. Built for 12 months of structural transformation.
              </p>
            </RevealText>
          </div>
        </div>
      </section>

      {/* ===== INTRO ===== */}
      <section className="bg-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <RevealSection>
            <p className="font-display text-2xl md:text-[2.25rem] text-vine-primary font-light leading-relaxed">
              The Advisory Circle is not a program you consume.
            </p>
            <p className="font-display text-2xl md:text-[2.25rem] text-vine-primary/50 font-light leading-relaxed mt-3">
              It is an operating rhythm you enter.
            </p>
          </RevealSection>

          <RevealSection delay={200}>
            <div className="mt-16">
              <p className="section-label mb-6">Members receive a complete advisory infrastructure designed to:</p>
            <BulletList items={[
              "Compress decisions",
              "Reduce operational fragility",
              "Clarify revenue architecture",
              "Install governable systems",
              "Decrease cognitive load"
            ]} />
            </div>
          </RevealSection>

          <RevealSection delay={300}>
            <p className="mt-14 text-vine-primary/45 text-[15px] leading-relaxed border-l-2 border-copper/30 pl-6">
              Privacy is embedded. Structure is consistent. Momentum compounds.
            </p>
          </RevealSection>
        </div>
      </section>

      {/* ===== THE STRUCTURE ===== */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-24 md:pt-32">
          <SectionLabel>The Structure</SectionLabel>
          <h2 className="font-display text-3xl md:text-4xl text-vine-primary font-light leading-tight mt-4">
            Six integrated layers.
          </h2>
        </div>

        {/* Private Pulse */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Private Pulse</h3>
            <p className="section-label mt-2">Monthly, 1:1</p>

            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                One advisory session per month. 15–20 minutes.
              </p>
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                Short by design. Focused by necessity.
              </p>
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                We work on one decision that materially impacts your business in the next 14–30 days.
              </p>
            </div>

            <div className="mt-10">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">You leave with:</p>
              <BulletList items={[
                "One clear next move",
                "One structural correction",
                "One behavior or assumption to stop"
              ]} />
            </div>

            <div className="mt-8 pl-5 border-l border-vine-neutral/20">
              <p className="text-vine-primary/45 text-[14px] leading-relaxed">
                No storytelling. No performance. No recording.
              </p>
            </div>

            <ClosingLine>
              Over time, these pulses create decision discipline — not just decision speed.
            </ClosingLine>
          </div>
        </div>

        {/* Operator Lounge */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Operator Lounge</h3>
            <p className="section-label mt-2">Twice Monthly, Optional + Recorded</p>

            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                The Lounge is not a hot seat.
              </p>
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                It is a strategic briefing built from patterns across the membership. We translate private advisory work into generalized systems thinking — without exposing anyone.
              </p>
            </div>

            <div className="mt-10">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">Each session includes:</p>
              <BulletList items={[
                "3–5 structural patterns emerging across operators",
                "A systems framework",
                "An implementation template",
                "Recording access"
              ]} />
            </div>

            <div className="mt-8 pl-5 border-l border-vine-neutral/20">
              <p className="text-vine-primary/45 text-[14px] leading-relaxed">
                You may attend live or watch later. Camera optional. Participation optional.
              </p>
            </div>

            <ClosingLine>
              The value is in pattern recognition — not public sharing.
            </ClosingLine>
          </div>
        </div>

        {/* Quarterly Boardroom Review */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Quarterly Boardroom Review</h3>

            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                Once per quarter, you step out of daily motion.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
              <div>
                <p className="text-vine-primary/70 text-[14px] font-medium mb-3">You submit:</p>
                <BulletList items={["Revenue range", "Primary constraint", "90-day strategic bet"]} />
              </div>
              <div>
                <p className="text-vine-primary/70 text-[14px] font-medium mb-3">You receive:</p>
                <BulletList items={["Strategic correction", "Priority alignment", "Systems direction"]} />
              </div>
            </div>

            <div className="mt-10 space-y-3">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed font-medium">
                This is a forcing function.
              </p>
              <p className="text-vine-primary/50 text-[15px] leading-relaxed">
                Many operators are capable of action. Few operate within disciplined review cycles.
              </p>
            </div>

            <ClosingLine>This is where drift ends.</ClosingLine>
          </div>
        </div>

        {/* Monthly Operator Packet */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Monthly Operator Packet</h3>

            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                Delivered at the beginning of each month.
              </p>
              <p className="text-vine-primary/50 text-[15px] leading-relaxed">
                Not content. Not theory.
              </p>
            </div>

            <div className="mt-10">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">Each packet includes:</p>
              <BulletList items={[
                "A one-page operating plan",
                "KPI focus guidance",
                "A decision template",
                "A systems framework distilled from real advisory patterns"
              ]} />
            </div>

            <p className="mt-8 text-vine-primary/60 text-[15px] leading-relaxed">
              This ensures implementation between conversations.
            </p>

            <ClosingLine>
              The Circle is not built around inspiration. It is built around structure.
            </ClosingLine>
          </div>
        </div>

        {/* Decision Channel */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Decision Channel</h3>
            <p className="section-label mt-2">Asynchronous Support</p>

            <div className="mt-8 space-y-4">
              <p className="text-vine-primary/70 text-[15px] leading-relaxed">
                When a decision cannot wait for your next Pulse:
              </p>
              <p className="text-vine-primary/60 text-[15px] leading-relaxed">
                Submit it using the structured template.
              </p>
            </div>

            <div className="mt-8">
              <p className="text-vine-primary/70 text-[14px] font-medium mb-3">Receive:</p>
              <BulletList items={[
                "Clear direction",
                "Structural correction",
                "48-hour response window"
              ]} />
            </div>

            <div className="mt-8 pl-5 border-l border-vine-neutral/20">
              <p className="text-vine-primary/45 text-[14px] leading-relaxed">
                High signal only. No sprawling threads.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Commitment */}
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pb-32">
          <div className="border-t border-vine-neutral/15 pt-14">
            <h3 className="font-display text-2xl md:text-[28px] text-vine-primary font-normal">Privacy Commitment</h3>

            <p className="mt-8 text-vine-primary/70 text-[15px] leading-relaxed">
              This structure was designed for adults with responsibilities.
            </p>

            <div className="mt-8 space-y-3">
              {[
                "Camera never required",
                "Public sharing never required",
                "Private sessions never recorded",
                "Lounge sessions anonymized",
                "Chatham House Rule across all interactions"
              ].map((item) => (
                <p key={item} className="text-vine-primary/60 text-[15px] pl-5 border-l-2 border-copper/25">
                  {item}
                </p>
              ))}
            </div>

            <p className="mt-8 text-vine-primary/50 text-[14px] font-medium">
              You control your visibility.
            </p>
          </div>
        </div>
      </section>

      {/* ===== 90-DAY EXPERIENCE MAP ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <SectionLabel>The 90-Day Experience Map</SectionLabel>
          <p className="mt-4 text-vine-neutral/60 text-[15px] max-w-2xl leading-relaxed">
            This is a 12-month structure. But the first 90 days matter.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-16">
            {/* Month 1 */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium">Month 1</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <h3 className="font-display text-2xl font-light mb-6">Decision Stabilization</h3>
              <BulletListLight items={[
                "Identify revenue leaks",
                "Clarify your true constraint",
                "Stop reactive overrides",
                "Establish advisory rhythm"
              ]} />
              <p className="mt-6 text-vine-neutral/40 text-[14px] italic">
                Noise reduces. Focus sharpens.
              </p>
            </div>

            {/* Month 2 */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium">Month 2</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <h3 className="font-display text-2xl font-light mb-6">Systems Tightening</h3>
              <BulletListLight items={[
                "Strengthen operational integrity",
                "Clarify role boundaries",
                "Improve sequencing",
                "Reduce drift between strategy and execution"
              ]} />
              <p className="mt-6 text-vine-neutral/40 text-[14px] italic">
                Activity becomes structured.
              </p>
            </div>

            {/* Month 3 */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium">Month 3</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <h3 className="font-display text-2xl font-light mb-6">Controlled Scaling</h3>
              <BulletListLight items={[
                "Board-level correction",
                "Refined 90-day priorities",
                "Fewer reactive decisions",
                "Stronger governance"
              ]} />
              <p className="mt-6 text-vine-neutral/40 text-[14px] italic">
                You begin operating from systems instead of urgency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6-MONTH & 12-MONTH ARC ===== */}
      <section className="bg-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          {/* 6-Month */}
          <div>
            <SectionLabel>6-Month Arc</SectionLabel>
            <p className="text-vine-primary/70 text-[15px] leading-relaxed mt-4 mb-8">
              By month six, members typically experience:
            </p>
            <BulletList items={[
              "Reduced volatility",
              "Cleaner sales motion",
              "Improved margin visibility",
              "Lower emotional load",
              "More defensible decisions"
            ]} />
            <p className="mt-8 font-display text-xl text-vine-primary/70 font-light">
              Momentum becomes governable.
            </p>
          </div>

          <CopperRule />

          {/* 12-Month */}
          <div>
            <SectionLabel>12-Month Transformation</SectionLabel>
            <p className="text-vine-primary/70 text-[15px] leading-relaxed mt-4 mb-8">
              At twelve months, the change is structural:
            </p>
            <BulletList items={[
              "Decision-making becomes disciplined",
              "Revenue architecture is clearer",
              "Operational fragility decreases",
              "Hiring and vendor choices are more defensible",
              "Growth is less chaotic"
            ]} />
            <div className="mt-10">
              <p className="font-display text-2xl text-vine-primary font-light leading-relaxed">
                The business is not louder.
              </p>
              <p className="font-display text-2xl text-copper font-light">
                It is stronger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOUNDARIES ===== */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <SectionLabel>Boundaries</SectionLabel>
          <p className="text-vine-primary/60 text-[15px] mt-4 mb-10">Clarity requires edges.</p>

          <div className="space-y-8">
            {[
              { not: "This is not a mastermind.", because: "There is no required sharing." },
              { not: "This is not coaching.", because: "There is no motivational component." },
              { not: "This is not a community.", because: "There is no social layer." },
              { not: "This is not content.", because: "There is no course to complete." },
            ].map((item, i) => (
              <div key={i} className="pb-2">
                <p className="text-vine-primary/75 text-[16px] font-medium">{item.not}</p>
                <p className="text-vine-primary/40 text-[14px] mt-1">{item.because}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-copper/20">
            <p className="text-vine-primary text-[15px] font-medium leading-relaxed">
              This is a governed advisory structure with clear deliverables and privacy defaults.
            </p>
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU ARE ACTUALLY BUYING ===== */}
      <section className="bg-vine-deep text-vine-warm section-spacer-lg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <SectionLabel>What You Are Actually Buying</SectionLabel>

          <div className="mt-8">
            <p className="text-vine-neutral/60 text-[14px] font-medium mb-4">You are buying:</p>
            <BulletListLight items={[
              "A structured decision rhythm",
              "An external operating lens",
              "Revenue system correction",
              "Risk reduction",
              "Accountability without exposure",
              "Strategic isolation solved"
            ]} />
          </div>

          <div className="mt-12 pt-8 border-t border-white/[0.08]">
            <p className="font-display text-xl text-vine-warm/80 font-light leading-relaxed">
              You are not buying access to time.
            </p>
            <p className="font-display text-xl text-copper font-light mt-1">
              You are buying disciplined momentum.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <SectionLabel>Frequently Asked</SectionLabel>
          <h2 className="font-display text-3xl text-vine-primary font-light mb-10 mt-4">
            Common questions.
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqItems.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-vine-neutral/15 bg-white/40 px-6">
                <AccordionTrigger className="text-vine-primary text-[15px] font-normal py-5 hover:no-underline hover:text-copper transition-colors text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <div className="space-y-3">
                    {faq.blocks.map((block, j) => {
                      if (typeof block === 'string') {
                        return (
                          <p key={j} className="text-vine-primary/60 text-[14px] leading-relaxed">
                            {block}
                          </p>
                        );
                      }
                      if (block.list) {
                        return (
                          <div key={j}>
                            {block.label && (
                              <p className="text-vine-primary/70 text-[14px] font-medium mb-2">{block.label}</p>
                            )}
                            <div className="space-y-1.5 ml-1">
                              {block.list.map((item, k) => (
                                <div key={k} className="flex items-start gap-3">
                                  <div className="w-1 h-1 rounded-full bg-copper mt-2 flex-shrink-0" />
                                  <p className="text-vine-primary/55 text-[14px] leading-relaxed">{item}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 text-center">
          <div className="w-12 h-px bg-copper mx-auto mb-8" />
          <h2 className="font-display text-3xl text-vine-warm font-light">Ready to apply?</h2>
          <p className="mt-4 text-vine-neutral/50 text-[15px]">Membership is by application. This room is limited to preserve signal.</p>
          <Link
            to="/apply"
            className="mt-8 inline-flex items-center gap-2 px-10 py-4 bg-copper text-white text-[13px] tracking-[0.12em] uppercase btn-copper"
          >
            Apply Now <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
